// ConfirmCardPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar2 from "../components/navbar/Navbar2";
import AOS from "aos";
import "aos/dist/aos.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { api, socket } from "../api";
import LoadingPage from "./LoadingPage";
import { useTranslation } from "react-i18next";
import { parseJWT } from "../utils/jwtUtils";
import Footer from "../components/navbar/Footer";

const ConfirmCardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const room_id = searchParams.get("room");
  const stake = searchParams.get("stake");
  const winAmount = searchParams.get("win");
  const [loading, setLoading] = useState(true); // Loading state

  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const userInfo = parseJWT(token?.token);

  // const userInfo = localStorage.getItem("userInfo")
  //   ? JSON.parse(localStorage.getItem("userInfo"))
  //   : null;

  const phone = userInfo?.phone;

  const [timeLeft, setTimeLeft] = useState(null);
  const [cardGrid, setCardGrid] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false); // Prevent auto-release

  useEffect(() => {
    AOS.init();
    AOS.refresh();
  }, []);

  // 🟢 Join room
  useEffect(() => {
    if (!room_id || !stake || !phone) return;
    socket.emit("join-room", { roomId: room_id, amount: stake, phone });
  }, [room_id, stake, phone]);

  // 🎯 Countdown + game start
  useEffect(() => {
    const handleCountdown = ({ roomId: rid, countdown }) => {
      if (rid === room_id) setTimeLeft(countdown);
    };

    const handleGameStart = ({ roomId: rid }) => {
      if (rid === room_id) {
        navigate(`/play?room=${room_id}`);
      }
    };

    socket.on("room-countdown", handleCountdown);
    socket.on("game-started", handleGameStart);

    return () => {
      socket.off("room-countdown", handleCountdown);
      socket.off("game-started", handleGameStart);
    };
  }, [room_id, navigate]);

  // ------------------------
  // Load card grid for THIS ROOM only
  // ------------------------
  useEffect(() => {
    if (!id || !room_id) return;

    setLoading(true);

    api
      .get(`/api/cards/${id}?roomId=${room_id}`) // Pass roomId to backend
      .then((res) => {
        let gridData = res.data.grid;

        // Fix: backend may send stringified grid
        if (typeof gridData === "string") {
          try {
            gridData = JSON.parse(gridData);
          } catch (err) {
            //  console.error("Failed to parse grid", err);
            gridData = [];
          }
        }

        setCardGrid(gridData);
        //  console.log("✅ Raw cardGrid from API:", gridData);
      })
      .catch((err) => {
        // console.error("Failed to load card grid", err);
        toast.error("Failed to load card", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .finally(() => setLoading(false));
  }, [id, room_id]);

  // ------------------------
  // Socket events
  // ------------------------
  useEffect(() => {
    const handleSuccess = ({ message, newBalance }) => {
      setIsConfirmed(true); // prevent auto-release

      toast.success(message || "Card confirmed!", {
        position: "top-center",
        autoClose: 2000,
      });

      // Update local storage balance
      if (userInfo) {
        // Keep the token intact in localStorage
        const token = localStorage.getItem("userInfo")
          ? JSON.parse(localStorage.getItem("userInfo")).token
          : null;

        // Store updated balance separately in localStorage or state
        // Here we use a separate key to avoid overwriting the token
        localStorage.setItem("balance", newBalance);

        // Or just update state if you have a balance state variable
        // setBalance(newBalance);
      }

      navigate(`/select-cards?room=${room_id}&stake=${stake}&win=${winAmount}`);
    };

    const handleFailed = ({ reason, currentBalance, required }) => {
      if (reason === "Insufficient balance") {
        toast.error(
          `Insufficient balance! You have ${currentBalance} Birr, need ${required} Birr.`,
          { position: "top-center", autoClose: 3000 },
        );
      } else {
        toast.error(reason || "Card reservation failed!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    };

    socket.on("card-confirmed-success", handleSuccess);
    socket.on("card-reserve-failed", handleFailed);

    return () => {
      socket.off("card-confirmed-success", handleSuccess);
      socket.off("card-reserve-failed", handleFailed);
    };
  }, [navigate, room_id, stake, winAmount, userInfo]);

  // ------------------------
  // Confirm / Cancel handlers
  // ------------------------
  const handleConfirm = () => {
    socket.emit("confirm-card", {
      number: parseInt(id),
      roomId: room_id, // pass roomId
      phone,
    });
  };

  const handleCancel = () => {
    if (!id || !room_id) return;

    socket.emit("cancel-reservation", {
      number: parseInt(id, 10),
      roomId: room_id, // ✅ fixed
    });

    setTimeout(() => {
      navigate(`/select-cards?room=${room_id}&stake=${stake}&win=${winAmount}`);
    }, 300);
  };

  // ------------------------
  // Release card on browser close / refresh / disconnect
  // ------------------------
  useEffect(() => {
    const cardNumber = parseInt(id, 10);

    const releaseOnClose = () => {
      if (!isConfirmed && cardNumber && room_id) {
        socket.emit("cancel-reservation", {
          number: cardNumber,
          roomId: room_id,
        });
      }
    };

    window.addEventListener("beforeunload", releaseOnClose);

    const releaseOnDisconnect = () => {
      if (!isConfirmed && cardNumber && room_id) {
        socket.emit("cancel-reservation", {
          number: cardNumber,
          roomId: room_id,
        });
      }
    };

    socket.on("disconnect", releaseOnDisconnect);

    return () => {
      window.removeEventListener("beforeunload", releaseOnClose);
      socket.off("disconnect", releaseOnDisconnect);

      if (!isConfirmed && cardNumber && room_id) {
        socket.emit("cancel-reservation", {
          number: cardNumber,
          roomId: room_id,
        });
      }
    };
  }, [id, isConfirmed, room_id]);

  const { t: translate } = useTranslation(["ConfirmCardPage"]);
  if (loading) return <LoadingPage />;

  return (
    <>
      <div className="fixed left-0 right-0 top-0 z-50 bg-gradient-to-l from-cyan-900 via-cyan-800 to-cyan-900">
        <Navbar2 />
      </div>

      <div className="min-h-screen bg-primary flex flex-col items-center justify-center text-white pt-20 md:pt-20">
        <div className="text-4xl font-mono mb-1 text-blue-400">
          0:{(timeLeft ?? 0).toString().padStart(2, "0")}
        </div>

        <div className="bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] px-6 py-1 rounded-full mb-2 font-serif">
          {stake} {translate("Birr_Per")}
        </div>

        <div
          className="bg-[#134454] p-4 rounded-lg text-center shadow-xl"
          data-aos="zoom-in"
        >
          <h2 className="text-xl mb-2 font-serif">
            {translate("Card_No")}
            {id}
          </h2>

          <div className="flex justify-center space-x-3 mb-3 font-serif">
            {["B", "I", "N", "G", "O"].map((l, i) => (
              <div
                key={i}
                className={`w-8 h-8 flex items-center justify-center font-bold text-lg rounded-full ${
                  i === 2 ? "bg-red-500" : "bg-[#c9c306]"
                }`}
              >
                {l}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-5 gap-2">
            {cardGrid.flat().map((num, i) => (
              <div
                key={i}
                className={`w-10 h-10 flex items-center justify-center rounded-md font-bold ${
                  num === "F"
                    ? "bg-blue-800 text-white"
                    : "bg-[#073440] text-[#e6f7f9]"
                }`}
              >
                {num === "F" ? "★" : num}
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-4 gap-4">
            <button
              onClick={handleCancel}
              className="bg-gray-200 text-2xl text-black px-4 py-1 rounded-md font-serif"
            >
              {translate("Go_Back")}
            </button>
            <button
              onClick={handleConfirm}
              className="bg-blue-800 text-2xl px-4 py-1 rounded-md hover:bg-blue-600 transition font-serif"
            >
              {translate("Confirm_Card")}
            </button>
          </div>
        </div>
        <Footer />
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </>
  );
};

export default ConfirmCardPage;
